/**
 * Service Listing with Image — image swap + sequential multi-line underline.
 *
 * On hover of any service item:
 *   - The left image fades to that service's featured image (120ms fade)
 *   - The hovered item gets the --active class (persistent until another hovered)
 *   - The hovered item's service name underline sweeps left → right, per line
 *   - The previously active item's underline retracts in reverse order (line 2 first)
 *
 * Underlines are built via getClientRects() on the inline <a> element, which
 * returns one DOMRect per line box. A <span class="__service-name-line"> is
 * absolutely positioned per line inside a __service-name-wrap container.
 */

document.addEventListener("DOMContentLoaded", () => {
  const LINE_DURATION = 0.32; // seconds per line sweep
  const LINE_EASE = "cubic-bezier(0.22, 0.61, 0.36, 1)";
  const UNDERLINE_OFFSET = 2; // px below each line box bottom

  const blocks = document.querySelectorAll(".service-listing-with-image");
  if (!blocks.length) return;

  blocks.forEach((block) => {
    const imgLayers = block.querySelectorAll(".service-listing-with-image__image");
    const items = block.querySelectorAll(".service-listing-with-image__service-item");

    if (imgLayers.length < 2 || !items.length) return;

    // Front layer = currently visible (.is-active). Back layer = next src target.
    let frontImg = imgLayers[0];
    let backImg = imgLayers[1];

    // lineSpansByItem maps each __service-item to its array of underline spans.
    const lineSpansByItem = new Map();

    // Wrap each service name link and build per-line underline spans.
    items.forEach((item) => {
      const name = item.querySelector(".service-listing-with-image__service-name");
      if (!name) return;

      const wrap = document.createElement("div");
      wrap.className = "service-listing-with-image__service-name-wrap";
      name.parentNode.insertBefore(wrap, name);
      wrap.appendChild(name);

      let lineSpans = [];

      const buildLines = () => {
        lineSpans.forEach((s) => s.remove());
        lineSpans = [];

        const nameRects = Array.from(name.getClientRects());
        const wrapRect = wrap.getBoundingClientRect();

        nameRects.forEach((rect, i) => {
          const span = document.createElement("span");
          span.className = "service-listing-with-image__service-name-line";
          span.style.left = `${rect.left - wrapRect.left}px`;
          span.style.top = `${rect.bottom - wrapRect.top + UNDERLINE_OFFSET}px`;
          span.style.width = `${rect.width}px`;
          span.style.transition = `transform ${LINE_DURATION}s ${LINE_EASE} ${i * LINE_DURATION}s`;
          wrap.appendChild(span);
          lineSpans.push(span);
        });

        lineSpansByItem.set(item, lineSpans);
      };

      buildLines();

      let resizeTimer;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(buildLines, 120);
      });
    });

    // Animate underlines in — forward order (line 1 then line 2).
    const showUnderlines = (item) => {
      const spans = lineSpansByItem.get(item) || [];
      spans.forEach((span, i) => {
        span.style.transition = `transform ${LINE_DURATION}s ${LINE_EASE} ${i * LINE_DURATION}s`;
        span.style.transform = "scaleX(1)";
      });
    };

    // Animate underlines out — reverse order (line 2 retracts first).
    const hideUnderlines = (item) => {
      const spans = lineSpansByItem.get(item) || [];
      const total = spans.length;
      spans.forEach((span, i) => {
        const reverseDelay = (total - 1 - i) * LINE_DURATION;
        span.style.transition = `transform ${LINE_DURATION}s ${LINE_EASE} ${reverseDelay}s`;
        span.style.transform = "scaleX(0)";
      });
    };

    // Show underlines immediately with no animation (used on page load for item 1).
    const initUnderlines = (item) => {
      const spans = lineSpansByItem.get(item) || [];
      spans.forEach((span) => {
        span.style.transition = "none";
        span.style.transform = "scaleX(1)";
      });
    };

    // Initialise first item as active.
    let activeItem = items[0];
    items[0].classList.add("service-listing-with-image__service-item--active");
    initUnderlines(items[0]);

    // Cross-fade is locked while a transition is in flight so that rapid
    // hovers can't reset the back layer's src mid-fade (which would snap the
    // visible image instead of fading). New hovers received during a fade
    // overwrite a single pending slot and run when the current fade ends.
    let isFading = false;
    let pendingSrc = null;
    let pendingAlt = "";

    const crossFadeTo = (newSrc, newAlt) => {
      if (!newSrc || newSrc === frontImg.src) return;

      if (isFading) {
        pendingSrc = newSrc;
        pendingAlt = newAlt;
        return;
      }

      isFading = true;
      backImg.alt = newAlt;
      backImg.src = newSrc;

      const startFade = () => {
        backImg.classList.add("is-active");
        frontImg.classList.remove("is-active");
        const prevFront = frontImg;
        frontImg = backImg;
        backImg = prevFront;

        // Wait for the opacity transition to finish on the new front layer,
        // then release the lock and replay any queued hover.
        const onEnd = (e) => {
          if (e.propertyName !== "opacity" || e.target !== frontImg) return;
          frontImg.removeEventListener("transitionend", onEnd);
          isFading = false;
          if (pendingSrc && pendingSrc !== frontImg.src) {
            const nextSrc = pendingSrc;
            const nextAlt = pendingAlt;
            pendingSrc = null;
            pendingAlt = "";
            crossFadeTo(nextSrc, nextAlt);
          } else {
            pendingSrc = null;
            pendingAlt = "";
          }
        };
        frontImg.addEventListener("transitionend", onEnd);
      };

      if (backImg.complete && backImg.naturalWidth) {
        startFade();
      } else {
        backImg.addEventListener("load", startFade, { once: true });
        backImg.addEventListener("error", startFade, { once: true });
      }
    };

    const setActiveItem = (targetItem) => {
      if (targetItem === activeItem) return;

      // Retract underlines on the outgoing item.
      hideUnderlines(activeItem);

      // Switch active class.
      items.forEach((item) =>
        item.classList.remove("service-listing-with-image__service-item--active")
      );
      targetItem.classList.add("service-listing-with-image__service-item--active");
      activeItem = targetItem;

      // Animate underlines in on the incoming item.
      showUnderlines(targetItem);

      // Cross-fade the image to the hovered service's featured image.
      crossFadeTo(targetItem.dataset.imgSrc, targetItem.dataset.imgAlt || "");
    };

    items.forEach((item) => {
      item.addEventListener("mouseenter", () => setActiveItem(item));
    });

    // ---------------------------------------------------------------------
    // Navigation safeguard (mobile / tablet only) — on bfcache restore,
    // reset the image and active state back to the FIRST service item.
    //
    // Why mobile/tablet only:
    //   Desktop uses real hover so the image swap fires cleanly before any
    //   click/navigation; the original behaviour works correctly there.
    //   On mobile/tablet, a tap fires a synthetic mouseenter immediately
    //   before the link's navigation, causing the fade timeout to be mid-
    //   flight when the page is cached by bfcache — on restore the <img>
    //   is left with a broken/aborted src.
    //
    // Breakpoint: < 1025px (theme's $desktop-min) covers mobile + tablet.
    // matchMedia is evaluated at pageshow time so orientation / resize
    // between load and restore still resolves correctly.
    // ---------------------------------------------------------------------

    const resetToFirstItem = () => {
      // Cancel any in-flight fade and clear the queue.
      isFading = false;
      pendingSrc = null;
      pendingAlt = "";

      const first = items[0];
      const firstSrc = first.dataset.imgSrc;
      const firstAlt = first.dataset.imgAlt || "";

      // Reset layer A to the first item's image and make it the visible layer;
      // clear layer B.
      if (firstSrc) {
        imgLayers[0].src = firstSrc;
        imgLayers[0].alt = firstAlt;
      }
      imgLayers[0].classList.add("is-active");
      imgLayers[1].classList.remove("is-active");
      imgLayers[1].removeAttribute("src");
      imgLayers[1].alt = "";
      frontImg = imgLayers[0];
      backImg = imgLayers[1];

      // Retract any underlines on items that are not the first.
      items.forEach((item) => {
        if (item !== first) {
          hideUnderlines(item);
        }
      });

      // Reset active class to first item only.
      items.forEach((item) =>
        item.classList.remove("service-listing-with-image__service-item--active")
      );
      first.classList.add("service-listing-with-image__service-item--active");
      activeItem = first;

      // Always re-apply underlines to the first item — bfcache may have
      // preserved them in any state, so force them back on regardless.
      initUnderlines(first);
    };

    window.addEventListener("pageshow", (event) => {
      if (!event.persisted) return;

      // Skip on desktop (>= 1025px) — keep original behaviour there.
      const isMobileOrTablet = window.matchMedia("(max-width: 1024.98px)").matches;
      if (!isMobileOrTablet) return;

      resetToFirstItem();
    });
  });
});
